/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file TreatmentOptionsView.jsx
 * @author Daniel Aguirre
 * @description TreatmentOptions View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DashboardCard from 'components/DashboardCard';
import { useHistory } from 'react-router';
import DashboardDoughnut from 'components/Charts/DashboardDoughnut';
import styleGuideColors from 'commons/styleGuideColors';
import useTruncateAndFormat from 'hook/useTruncateAndFormat';

/**
 * TreatmentOptions View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function TreatmentOptionsView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);

  const { getTreatmentOptions } = dashboardStaffActions;

  const onGetData = (_, resp) => {
    let data = [];
    const classifications = [
      {
        label: 'Diálisis peritoneal',
        classification: 'Opciones de Tto: Diálisis Peritoneal',
        color: styleGuideColors.baxterBrandTeal
      },
      {
        label: 'Hemodiálisis',
        classification: 'Opciones de Tto: Hemodiálisis',
        color: styleGuideColors.baxterBrandBaxterBlue
      },
      {
        label: 'Manejo conservador',
        classification:
          'Opciones de Tto: Manejo Conservador (No indicación por criterio médico/ disentimiento)',
        color: styleGuideColors.baxterBrandCyan
      },
      {
        label: 'Actualmente no acepta diálisis',
        classification: 'Opciones de Tto: Actualmente no acepta diálisis',
        color: styleGuideColors.graphYellow
      },
      {
        label: 'Sin definir',
        classification: 'Opciones de Tto: Sin Definir',
        color: styleGuideColors.baxterBrandPurple
      },
      {
        label: 'Sin registro',
        classification: 'Sin Registro',
        color: styleGuideColors.graphGreyStrokes
      }
    ];

    let total = 0;

    if (resp?.data) {
      resp.data.forEach(({ cantidad }) => {
        total += cantidad;
      });
      classifications.forEach(({ label, color, classification }) => {
        const respElement = resp.data.find(
          ({ clasificacion }) => clasificacion === classification
        );
        if (respElement) {
          const value = (respElement.cantidad * 100) / total;
          data = [
            ...data,
            {
              value: useTruncateAndFormat(value, 2),
              label,
              classification,
              color,
              cantidad: respElement.cantidad
            }
          ];
        }
      });
      setChartData(data);
    }
  };

  useEffect(() => {
    setChartData(null);
    getTreatmentOptions({ clinicId }, onGetData);
  }, [translate, clinicId]);

  const handleDetailClick = (index) => {
    const { classification, cantidad: totalItems } = chartData[index];
    history.push(`/main/dashboard-detail/treatment-options`, {
      classification,
      totalItems
    });
  };

  return (
    <DashboardCard id="F23" loading={!chartData}>
      <DashboardDoughnut
        title={translate('dashboardStaff.treatmentOptions')}
        dataValues={chartData}
        onDetailClick={handleDetailClick}
      />
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
TreatmentOptionsView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getTreatmentOptions: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default TreatmentOptionsView;
